import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Button } from '@monksflow/monks-ui';
import { EditorContent } from '@tiptap/react';
import clsx from 'clsx';
import { ArrowUpIcon } from 'lucide-react';
import { trimTrailingBlocks } from '../../lib/trim-trailing-blocks';
import { EMPTY_DOC } from '../../lib/emptyDoc';
import { useCommentEditor } from '../../lib/useCommentEditor';
import { useFocusOnNewKey } from '../../lib/useFocusOnNewKey';
import type { CommentInputProps } from './CommentInput.types';
import { useTranslation } from 'react-i18next';

const EMPTY_DIV = <div></div>;

function isDocEmpty(doc: Record<string, unknown>): boolean {
  const content = doc.content;
  if (!Array.isArray(content) || content.length === 0) return true;
  // A doc with childless block nodes (e.g. `[{ type: 'paragraph' }]`) is still
  // semantically empty — match TipTap's `editor.isEmpty` semantics so the
  // initial submit-button gating stays correct after we seed EMPTY_DOC with a
  // paragraph.
  return content.every((node) => {
    if (!node || typeof node !== 'object') return true;
    const inner = (node as { content?: unknown }).content;
    return !Array.isArray(inner) || inner.length === 0;
  });
}

export function CommentInput({
  disabled = false,
  placeholder,
  initialContent,
  submitLabel,
  topContent,
  composerAnchor,
  focusKey,
  composerPrefix,
  leftActions,
  onSave,
  onNodeClick,
  submitDisabled = false,
  mentionOptions,
}: CommentInputProps) {
  const { t } = useTranslation('projects');

  const [isPending, setIsPending] = useState(false);
  const [isEmpty, setIsEmpty] = useState(() => isDocEmpty(initialContent ?? EMPTY_DOC));
  const [isFocused, setIsFocused] = useState(false);

  const editor = useCommentEditor({
    content: initialContent ?? EMPTY_DOC,
    editable: !disabled,
    onNodeClick,
    onUpdate: ({ editor: e }) => {
      setIsEmpty(e.isEmpty);
    },
    mentionOptions,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    // The visible placeholder is aria-hidden, so the same words name the field.
    ariaLabel: placeholder,
  });

  const handleSubmit = useCallback(() => {
    if (isEmpty || isPending || submitDisabled) return;

    // Drop trailing emptiness before saving: blank trailing paragraphs AND a
    // trailing hard break inside the last paragraph. Cmd+Enter fires TipTap's
    // HardBreak (its default Mod-Enter binding) before this handler runs, so
    // "type, then Cmd+Enter" would otherwise persist a blank trailing line.
    const json = editor.getJSON() as { content?: Array<{ type?: string; content?: unknown[] }> };
    const nodes = trimTrailingBlocks(json.content ?? []);
    const content = { ...json, content: nodes };

    setIsPending(true);
    void onSave(content)
      .then(() => {
        editor.commands.clearContent();
        setIsEmpty(true);
      })
      .catch(() => {
        // Error handling is the caller's responsibility via the onSave promise
      })
      .finally(() => {
        setIsPending(false);
      });
  }, [editor, isEmpty, isPending, submitDisabled, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const canSubmit = !(isEmpty || isPending || submitDisabled || disabled);

  const actionsApi = useMemo(
    () => ({
      insertText: (text: string) => {
        editor.chain().focus().insertContent(text).run();
      },
      canSubmit,
      isSubmitting: isPending,
      isEmpty,
    }),
    [editor, canSubmit, isPending, isEmpty],
  );
  const renderedLeftActions =
    typeof leftActions === 'function' ? leftActions(actionsApi) : leftActions;

  // Selecting a section for commenting puts the caret straight in the composer, so the person
  // can type without hunting for the field. `preventScroll` keeps that from moving either the
  // rail or the document: they just clicked in the document and the view must stay put.
  useFocusOnNewKey(focusKey, () => {
    editor.view.dom.focus({ preventScroll: true });
    editor.commands.focus('end');
  });

  // Routes a click anywhere in the box to the editor. `mousedown` rather than `click` so the
  // caret lands before the browser resolves its own selection, and `preventDefault` stops it
  // placing that selection on the non-editable chrome we just clicked. Real controls are left
  // alone, so the anchor chip's clear control and the action bar still get their own clicks.
  const handleBoxMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button, a, input, textarea, [contenteditable="true"]')) return;
      event.preventDefault();
      // `preventScroll` matters because the composer lives in a rail that scrolls
      // independently of the document: focusing it must not jerk either one.
      editor.view.dom.focus({ preventScroll: true });
      editor.commands.focus('end');
    },
    [editor],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="flex w-full min-w-0 gap-3" onKeyDown={handleKeyDown}>
      {/*
        The whole box is the click target, the way a well-built field is: clicking the padding,
        the anchor row or the space beside the text puts the caret in the editor. Without this
        only the text line itself was live, and an anchor chip tall enough to fill a row made the
        composer look broken — the row under it is not the input, and clicking it did nothing.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a click shim over a real editable; the editor itself carries the semantics and keyboard access */}
      <div
        data-testid="comment-input-box"
        className="flex min-w-0 flex-1 cursor-text flex-col gap-2 rounded-lg border border-border-light bg-surface px-4 pt-4 pb-2"
        onMouseDown={handleBoxMouseDown}
      >
        {topContent}

        {/*
          The anchor chip sits on its own row above the editor, per the prototype, rather than
          floating inside the editor's scroll box. A float does not expand its parent, so a chip
          taller than one line overflows a clipping container and its clear control goes with it.
        */}
        {composerAnchor && (
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">{composerAnchor}</div>
        )}

        <div className="flex cursor-text gap-2">
          <div
            data-testid="comment-input-editor-scroll"
            className="relative min-w-0 min-h-4 max-h-30 flex-1 cursor-text overflow-y-auto overflow-x-hidden"
          >
            {/* Float the prefix so the chip sits inline at the start of the
                first line and the typed text wraps after/below it, rather than
                occupying a separate left gutter column. The wrapper carries the
                editor's own line metrics (text-sm leading-relaxed) so the float
                is exactly ONE line tall — otherwise a taller chip makes the
                second line wrap around it too. */}
            {composerPrefix && (
              <span className="float-left mr-2 text-sm leading-relaxed">{composerPrefix}</span>
            )}
            {isEmpty && !isFocused && placeholder && (
              <span
                aria-hidden="true"
                className={clsx(
                  'pointer-events-none text-sm text-muted-foreground',
                  // Float (not absolute) when a prefix is present so the
                  // placeholder sits after the chip instead of overlapping it.
                  composerPrefix ? 'float-left' : 'absolute left-0 top-0',
                )}
              >
                {placeholder}
              </span>
            )}
            <EditorContent editor={editor} />
          </div>
        </div>

        <div className="-mx-2 flex justify-between gap-2">
          {renderedLeftActions ?? EMPTY_DIV}

          <Button
            aria-label={submitLabel ?? t('projectComment.commentInput.submitComment')}
            size="sm"
            variant="default"
            className={submitLabel ? undefined : 'p-2'}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitLabel ? submitLabel : <ArrowUpIcon className="size-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
